import { chipTint, markInk } from '../design/tokens';
import type { Theme } from '../design/tokens';
import type { Folder } from '../entity/folders';
import { FolderGlyph, iconFor } from './FolderIcons';

/**
 * A folder's identity, before you read its name.
 *
 * **The tile says NOTEBOOK ([isNotebook]); the letter is only a fallback
 * identity.** A notebook is a filled tile whether or not it carries a colour —
 * one at the top may have neither, so the tile has to read without them. A
 * plain folder is the glyph alone: an empty tile beside a name reads as an
 * unchecked box.
 *
 * The letter renders only where the tile can hold type at the head's 11px
 * floor, which is `size >= 22`. Below that it would land at 7–9px, so it is
 * dropped rather than shrunk — filled-tile-against-bare-glyph still reads at
 * 13px, which is what the distinction actually rests on.
 *
 * A plain folder never draws an icon: one with an icon of its own is a
 * notebook by that rule.
 *
 * **Colour is inherited from the nearest ancestor; the ICON is not drawn
 * unless it is the folder's own.** Both are inherited in the model, matching
 * the app — but fifteen identical pine trees down a 300px rail say only
 * "these are all under ponder", which the shared tile colour already says
 * without spending the one slot that could carry something else. So an
 * inherited hue paints the tile and the letter or glyph fills the middle.
 *
 * Sizes in use: 13 (chips) · 16 (tree row) · 18 (index row) · 24 (collapsed
 * rail) · 26 (page head).
 */
export function Mark({ folder, size = 18, theme }: { folder: Folder; size?: number; theme: Theme }) {
  const Icon = folder.iconIsOwn ? iconFor(folder.icon) : null;
  const tint = folder.hue ? chipTint(theme, folder.hue) : null;
  /** The glyph or letter in the folder's mark tone, which reads on its own
   *  chip in both themes; never a raw colour. */
  const identityInk = folder.hue ? markInk(theme, folder.hue) : null;

  if (!folder.notebook) {
    return (
      <span className="fd-mark is-plain" style={{ width: size, height: size }}>
        <span
          style={{
            color: identityInk
              ? `color-mix(in srgb, ${identityInk} 80%, transparent)`
              : 'color-mix(in srgb, var(--ink) 30%, transparent)',
          }}
        >
          <FolderGlyph size={Math.round(size * 0.86)} />
        </span>
      </span>
    );
  }

  const letter = size >= 22 ? (folder.name.match(/\p{L}/u)?.[0]?.toUpperCase() ?? null) : null;

  return (
    <span
      className="fd-mark is-tile"
      style={{
        width: size,
        height: size,
        borderRadius: Math.max(4, Math.round(size * 0.28)),
        background: tint ? tint.fill : 'color-mix(in srgb, var(--ink) 7%, transparent)',
        borderColor: tint ? tint.border : 'color-mix(in srgb, var(--ink) 18%, transparent)',
      }}
    >
      {Icon ? (
        <span style={{ color: identityInk ?? 'var(--inkSoft)' }}>
          <Icon size={Math.round(size * 0.62)} />
        </span>
      ) : letter ? (
        <span
          className="fd-mark-letter t-metaMono"
          style={{ fontSize: Math.max(11, Math.round(size * 0.46)), color: identityInk ?? 'var(--inkSoft)' }}
        >
          {letter}
        </span>
      ) : (
        <span
          style={{
            color: identityInk
              ? `color-mix(in srgb, ${identityInk} 90%, transparent)`
              : 'color-mix(in srgb, var(--ink) 42%, transparent)',
          }}
        >
          <FolderGlyph size={Math.round(size * 0.66)} />
        </span>
      )}
    </span>
  );
}
